import { FiArrowRight, FiExternalLink, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import useAuth from "../../Hooks/useAuth";
import { Drawer, MascotLoadingOverlay } from "../shared";
import { categoryStyle, activityTitle } from "./activityStyle";
import SubjectChip from "./SubjectChip";
import { useMyActivityDetail } from "./useMyActivityApi";

const DATE_TIME = new Intl.DateTimeFormat("tr-TR", {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const SUBJECT_KIND_LABEL = { device: "Cihaz", firm: "Firma", user: "Kullanıcı" };

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-SemiBold text-gray-500">{title}</h4>
      {children}
    </section>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <span className="shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 break-words text-right text-gray-900">{children}</span>
    </div>
  );
}

/**
 * Organizma: Son İşlemler detay çekmecesi (#412). `item` listedeki kalem —
 * başlık/zaman/konu hemen gösterilir, ayrıntılar `getMyActivityDetail` ile gelir.
 */
export default function ActivityDetailDrawer({ item, onClose }) {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { data, isLoading, isError, refetch } = useMyActivityDetail(item?._id);
  const detail = data?.data;
  const view = detail || item || {};
  const style = categoryStyle(item?.category);
  const Icon = style.icon;
  const subject = detail?.subject || item?.subject;
  const failed = view.success === false;

  const openDevice = () => {
    onClose();
    // Bayide cihaz detayı ayrı rotada (Cihazlarım)
    const base = auth?.role === "dealer" ? "/Anasayfa/Cihazlarım" : "/Anasayfa/Cihazlar";
    navigate(`${base}/${subject.id}`);
  };

  return (
    <Drawer
      open={!!item}
      onClose={onClose}
      width="md"
      title={activityTitle(view)}
      subtitle={view.timestamp ? DATE_TIME.format(new Date(view.timestamp)) : undefined}
      titleIcon={
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ring-1 ${style.tone}`}>
          <Icon className="h-4 w-4" />
        </span>
      }
    >
      <Drawer.Body className="space-y-6 px-5 py-4">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {detail?.categoryLabel && (
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-600">{detail.categoryLabel}</span>
          )}
          <span
            className={`rounded-md px-2 py-0.5 font-Medium ${
              failed ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"
            }`}
          >
            {failed ? "Başarısız" : "Başarılı"}
          </span>
        </div>

        {(subject?.label || detail?.firm?.label) && (
          <Section title="İlgili kayıt">
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 px-3">
              {subject?.label && (
                <InfoRow label={SUBJECT_KIND_LABEL[subject.kind] || "Kayıt"}>
                  <span className="inline-flex items-center gap-2">
                    <SubjectChip subject={subject} />
                    {subject.kind === "device" && subject.id && (
                      <button
                        type="button"
                        onClick={openDevice}
                        className="inline-flex items-center gap-1 text-xs font-Medium text-indigo-600 hover:underline"
                      >
                        Cihaza git <FiExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </InfoRow>
              )}
              {detail?.firm?.label && <InfoRow label="Firma">{detail.firm.label}</InfoRow>}
            </div>
          </Section>
        )}

        {isLoading ? (
          <div className="relative min-h-[160px]">
            <MascotLoadingOverlay message="Ayrıntılar yükleniyor..." />
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-9 rounded-lg bg-gray-100 motion-safe:animate-pulse" />
              ))}
            </div>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p className="text-sm text-gray-600">Ayrıntılar alınamadı.</p>
            <button
              type="button"
              onClick={refetch}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiRefreshCw className="h-4 w-4" /> Tekrar dene
            </button>
          </div>
        ) : (
          detail && (
            <>
              {detail.error?.message && (
                <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {detail.error.message}
                </div>
              )}

              {detail.changes?.length > 0 && (
                <Section title="Değişiklikler">
                  <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                    {detail.changes.map((c, i) => (
                      <li key={i} className="px-3 py-2 text-sm">
                        {c.label ? (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-gray-500">{c.label}</span>
                            <span className="text-gray-400 line-through">{c.from ?? "—"}</span>
                            <FiArrowRight className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-Medium text-gray-900">{c.to ?? "—"}</span>
                          </div>
                        ) : (
                          <span className="text-gray-700">{c.to}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {detail.details?.length > 0 && (
                <Section title="Ayrıntılar">
                  <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 px-3">
                    {detail.details.map((d) => (
                      <InfoRow key={d.label} label={d.label}>
                        {d.value}
                      </InfoRow>
                    ))}
                  </div>
                </Section>
              )}

              {(detail.ipAddress || detail.client) && (
                <Section title="Oturum">
                  <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 px-3">
                    {detail.client && <InfoRow label="Tarayıcı">{detail.client}</InfoRow>}
                    {detail.ipAddress && <InfoRow label="IP adresi">{detail.ipAddress}</InfoRow>}
                  </div>
                </Section>
              )}
            </>
          )
        )}
      </Drawer.Body>
    </Drawer>
  );
}
